/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { DesktopSessionState } from '@poweralpha/common';
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { parsePath } from 'react-router-dom';
import { usePermissions } from '../auth';
import { DEFAULT_PAGE_TITLE } from '../react-components/usePageTitle';
import {
  desktopWindowStateApi,
  useDesktopSessionQuery,
  useSaveDesktopSessionMutation,
} from './api';
import { DesktopAppStateStoreContext } from './app-state/context';
import { DesktopAppStateStore } from './app-state/DesktopAppStateStore';
import {
  readDesktopJournal,
  removeDesktopJournal,
  writeDesktopJournal,
} from './app-state/sessionJournal';
import {
  type DesktopActions,
  DesktopActionsContext,
  DesktopContext,
  type DesktopContextValue,
  type OpenDesktopPathOptions,
} from './DesktopContext';
import { DesktopLayoutProvider } from './DesktopLayoutContext';
import { isProjectScopedApp, resolveDesktopWindowPath } from './desktopAppRouting';
import { useMemoryLog } from './diagnostics/memoryLog';
import { normalizeAppPath, resolveAppForPath } from './navigation/appPaths';
import type { DesktopRouterLocation } from './navigation/DesktopRouter';
import { type ShellNavigation, ShellNavigationContext } from './navigation/ShellNavigationContext';
import { hasAnyPermission, serviceApps } from './serviceApps';
import type { DesktopApp } from './types';
import { useWindowLayoutTransitions } from './use-window-layout-transitions';
import { normalizeDesktopProjectId } from './utils';
import {
  createInitialDesktopState,
  type DesktopState,
  type DesktopWindowAction,
  type DesktopWindowState,
  desktopReducer,
  getCurrentHistoryEntry,
  serializeDesktopState,
} from './windowSystem';
import { getWindowTabs } from './windowTabs';

const DESKTOP_LOCATION: DesktopRouterLocation = { key: 'desktop', path: '/' };

interface BootLocation {
  /** Page to open in a window, when the app was loaded from a service URL. */
  windowPath?: string;
  /** Desktop-level URL (e.g. `/?agentConversation=`) for shell consumers to read once. */
  shellPath?: string;
  projectId: string | null;
}

/** Reads the URL the app was loaded from. The URL is an entry point only; state lives in the desktop. */
const readBootLocation = (): BootLocation => {
  const path = normalizeAppPath(`${window.location.pathname}${window.location.search}`);
  const { pathname = '/', search = '' } = parsePath(path);
  if (pathname !== '/') {
    return { windowPath: path, projectId: null };
  }

  const params = new URLSearchParams(search);
  const projectId = normalizeDesktopProjectId(params.get('project'));
  params.delete('project');
  const shellSearch = params.toString();
  return { shellPath: shellSearch ? `/?${shellSearch}` : undefined, projectId };
};

// Permissions may still be loading at boot; the window layer hides apps the user cannot see.
const getBootWindowAction = (
  { windowPath }: BootLocation,
  selectedProjectId: string | null = null,
): DesktopWindowAction | undefined =>
  windowPath
    ? { type: 'open_path', apps: serviceApps, path: windowPath, replace: true, selectedProjectId }
    : undefined;

const withBootWindow = (state: DesktopState, bootLocation: BootLocation) => {
  const bootAction = getBootWindowAction(bootLocation);
  return bootAction ? desktopReducer(state, bootAction) : state;
};

/**
 * Owns desktop state: windows, their locations, selected project and widgets. Also provides shell
 * navigation for chrome outside windows (see DesktopShellRouter) and mirrors the
 * focused window location into the address bar so reload and shared links reopen it.
 */
export function DesktopProvider({ children }: Readonly<PropsWithChildren>) {
  const permissions = usePermissions();
  const [boot] = useState(readBootLocation);
  // A deep-linked page is part of the first render, so the default window never mounts (and
  // loads its code) only to be covered by it.
  const [desktopState, dispatchImmediately] = useReducer(desktopReducer, boot, (bootLocation) =>
    withBootWindow(createInitialDesktopState(serviceApps), bootLocation),
  );
  const dispatchDesktop = useWindowLayoutTransitions(dispatchImmediately);
  const [selectedProjectId, setSelectedProjectId] = useState(boot.projectId);
  const [shellPath, setShellPath] = useState(boot.shellPath);
  const [showWidgets, setShowWidgets] = useState(true);
  const desktopSessionQuery = useDesktopSessionQuery();
  const { mutate: saveDesktopSession } = useSaveDesktopSessionMutation();
  const hasLoadedPersistedSession = useRef(false);
  const [hasBooted, setHasBooted] = useState(false);
  const lastPersistedDesktopState = useRef<string | null>(null);
  const [appStateStore] = useState(() => new DesktopAppStateStore(desktopWindowStateApi));
  const pendingSave = useRef<(() => DesktopSessionState) | null>(null);
  const stateRef = useRef(desktopState);
  stateRef.current = desktopState;
  const userId = desktopSessionQuery.data?.user.id ?? null;

  const visibleApps = useMemo(
    () => serviceApps.filter((app) => hasAnyPermission(permissions, app.permissions)),
    [permissions],
  );

  useEffect(() => {
    if (hasBooted || (!desktopSessionQuery.data && !desktopSessionQuery.isError)) {
      return;
    }
    setHasBooted(true);
    if (!desktopSessionQuery.data) {
      // No session (e.g. no desktop tables locally): windows start without saved state.
      void appStateStore.start(null);
      return;
    }

    const serverDesktopState = desktopSessionQuery.data.session.desktop_state;
    const { id: sessionUserId } = desktopSessionQuery.data.user;
    // A change the debounced save missed (tab closed right after it) wins over the server's.
    const persistedDesktopState = readDesktopJournal(sessionUserId, serverDesktopState);
    removeDesktopJournal(sessionUserId);
    hasLoadedPersistedSession.current = true;
    lastPersistedDesktopState.current = JSON.stringify(serverDesktopState);
    // Windows load their saved view state once edits left over from the last page are sent.
    void appStateStore.start(sessionUserId);
    dispatchDesktop({ type: 'hydrate', desktopState: persistedDesktopState, apps: serviceApps });
    setShowWidgets(persistedDesktopState.showWidgets);
    const projectId =
      boot.projectId ?? normalizeDesktopProjectId(persistedDesktopState.selectedProjectId);
    setSelectedProjectId(projectId);
    // Hydrating replaces the window state, so reopen the deep-linked page on top of it
    // (batched with the hydrate into one render). A reload of a window that follows the selected
    // project reselects its tab instead of opening a second one.
    const bootAction = getBootWindowAction(boot, projectId);
    if (bootAction) {
      dispatchDesktop(bootAction);
    }
  }, [
    appStateStore,
    boot,
    desktopSessionQuery.data,
    desktopSessionQuery.isError,
    hasBooted,
    dispatchDesktop,
  ]);

  // Serializing the whole desktop is the expensive part, so it waits for the debounce too:
  // a burst of changes (focus, drops, route state) costs one snapshot.
  useEffect(() => {
    if (!hasLoadedPersistedSession.current) {
      return;
    }

    const serialize = () => serializeDesktopState({ desktopState, selectedProjectId, showWidgets });
    pendingSave.current = serialize;
    const timeoutId = window.setTimeout(() => {
      pendingSave.current = null;
      const serializedDesktopState = serialize();
      const serializedPayload = JSON.stringify(serializedDesktopState);
      if (serializedPayload === lastPersistedDesktopState.current) return;
      lastPersistedDesktopState.current = serializedPayload;
      saveDesktopSession({ desktop_state: serializedDesktopState });
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [desktopState, saveDesktopSession, selectedProjectId, showWidgets]);

  useEffect(() => {
    if (!userId) return;
    const keepUnsavedState = () => {
      if (pendingSave.current) writeDesktopJournal(userId, pendingSave.current());
      appStateStore.bufferPendingPatches();
    };
    // A hidden tab may never come back: send view state now instead of after the debounce.
    const saveWhenHidden = () => {
      if (document.visibilityState === 'hidden') void appStateStore.flush();
    };
    window.addEventListener('pagehide', keepUnsavedState);
    document.addEventListener('visibilitychange', saveWhenHidden);
    return () => {
      window.removeEventListener('pagehide', keepUnsavedState);
      document.removeEventListener('visibilitychange', saveWhenHidden);
    };
  }, [appStateStore, userId]);

  // Closing a window discards its app state and drafts.
  const openWindowIds = useRef<ReadonlySet<string>>(new Set());
  useEffect(() => {
    const nextOpenWindowIds = new Set(
      desktopState.windows
        .filter(({ isOpen }) => isOpen)
        .flatMap((window) => getWindowTabs(window).map(({ id }) => id)),
    );
    for (const windowId of openWindowIds.current) {
      if (!nextOpenWindowIds.has(windowId)) appStateStore.removeWindow(windowId);
    }
    openWindowIds.current = nextOpenWindowIds;
  }, [appStateStore, desktopState.windows]);

  const getWindowLocation = useCallback(
    (windowState: DesktopWindowState, app: DesktopApp): DesktopRouterLocation => {
      const entry = getCurrentHistoryEntry(windowState);
      return {
        key: entry.key,
        path: resolveDesktopWindowPath({ app, path: entry.path, selectedProjectId }),
        state: entry.state,
      };
    },
    [selectedProjectId],
  );

  const selectProject = useCallback(
    (projectId: string | null) => {
      const nextProjectId = normalizeDesktopProjectId(projectId);
      setSelectedProjectId(nextProjectId);
      if (nextProjectId) {
        setShowWidgets(true);
      }
      // Project-scoped windows follow the selected project again.
      for (const windowState of stateRef.current.windows) {
        const app = serviceApps.find(({ id }) => id === windowState.appId);
        if (app && isProjectScopedApp(app) && windowState.path !== app.defaultPath) {
          dispatchDesktop({
            type: 'navigate',
            id: windowState.id,
            path: app.defaultPath,
            replace: true,
          });
        }
      }
    },
    [dispatchDesktop],
  );

  const openPath = useCallback(
    (
      rawPath: string,
      { replace, sourceWindowId, sourceTabId, state }: OpenDesktopPathOptions = {},
    ) => {
      const path = normalizeAppPath(rawPath);
      const { pathname = '/', search = '' } = parsePath(path);
      // The desktop root is not a page. Only shell chrome (e.g. the agent deep link) may keep a
      // desktop-level URL; a window navigating "home" must not take over the address bar.
      if (pathname === '/') {
        const params = new URLSearchParams(search);
        const projectId = params.get('project');
        params.delete('project');
        if (projectId) selectProject(projectId);
        if (!sourceWindowId) {
          const shellSearch = params.toString();
          setShellPath(shellSearch ? `/?${shellSearch}` : undefined);
        }
        return;
      }
      if (!sourceWindowId && !resolveAppForPath(visibleApps, path)) {
        return;
      }
      // A page opens in a window, so the focused window owns the address bar again.
      setShellPath(undefined);
      dispatchDesktop({
        type: 'open_path',
        apps: visibleApps,
        path,
        replace,
        sourceWindowId,
        sourceTabId,
        state,
        selectedProjectId,
      });
    },
    [selectedProjectId, selectProject, visibleApps, dispatchDesktop],
  );

  const focusedWindow = desktopState.windows.find(
    (windowState) =>
      windowState.id === desktopState.focusedWindowId &&
      windowState.isOpen &&
      !windowState.isMinimized,
  );
  const focusedApp = visibleApps.find((app) => app.id === focusedWindow?.appId);
  const focusedWindowId = focusedApp ? focusedWindow?.id : undefined;
  const focusedTabId = focusedWindow?.selectedTabId ?? focusedWindowId;
  const shellLocation = useMemo(() => {
    if (shellPath) {
      return { key: 'shell', path: shellPath };
    }
    return focusedWindow && focusedApp
      ? getWindowLocation(focusedWindow, focusedApp)
      : DESKTOP_LOCATION;
  }, [focusedApp, focusedWindow, getWindowLocation, shellPath]);

  // Pages in windows report their title to the store; the focused window's is shown.
  useEffect(() => {
    const showTitle = () => {
      document.title =
        (focusedTabId && appStateStore.getTitle(focusedTabId)) ||
        (focusedApp ? `PowerAlpha - ${focusedApp.label}` : DEFAULT_PAGE_TITLE);
    };
    showTitle();
    return appStateStore.subscribeTitles(showTitle);
  }, [appStateStore, focusedApp, focusedTabId]);

  useEffect(() => {
    // Keep the load URL until the session and any deep link are applied.
    if (!hasBooted) {
      return;
    }
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentPath !== shellLocation.path) {
      window.history.replaceState(window.history.state, '', shellLocation.path);
    }
  }, [hasBooted, shellLocation.path]);

  const handleShellNavigate = useCallback(
    (path: string, state: unknown, replace: boolean) =>
      openPath(path, { replace, sourceWindowId: shellPath ? undefined : focusedWindowId, state }),
    [focusedWindowId, openPath, shellPath],
  );
  const handleShellGo = useCallback(
    (delta: number) => {
      if (focusedWindowId) {
        dispatchDesktop({ type: 'go', id: focusedWindowId, delta });
      }
    },
    [focusedWindowId, dispatchDesktop],
  );

  const contextValue = useMemo<DesktopContextValue>(
    () => ({
      desktopState,
      dispatchDesktop,
      getWindowLocation,
      isSessionReady: hasBooted,
      openPath,
      selectedProjectId,
      selectProject,
      setShowWidgets,
      showWidgets,
      visibleApps,
    }),
    [
      desktopState,
      dispatchDesktop,
      getWindowLocation,
      hasBooted,
      openPath,
      selectedProjectId,
      selectProject,
      showWidgets,
      visibleApps,
    ],
  );
  const actions = useMemo<DesktopActions>(
    () => ({
      dispatchDesktop,
      getDesktopState: () => stateRef.current,
      openPath,
      selectProject,
      visibleApps,
    }),
    [openPath, selectProject, visibleApps, dispatchDesktop],
  );
  useMemoryLog(desktopState);

  const shellNavigation = useMemo<ShellNavigation>(
    () => ({ go: handleShellGo, location: shellLocation, navigate: handleShellNavigate }),
    [handleShellGo, handleShellNavigate, shellLocation],
  );

  return (
    <DesktopActionsContext value={actions}>
      <DesktopContext.Provider value={contextValue}>
        <DesktopAppStateStoreContext value={appStateStore}>
          <ShellNavigationContext.Provider value={shellNavigation}>
            <DesktopLayoutProvider>{children}</DesktopLayoutProvider>
          </ShellNavigationContext.Provider>
        </DesktopAppStateStoreContext>
      </DesktopContext.Provider>
    </DesktopActionsContext>
  );
}
